import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import '@ant-design/compatible/assets/index.css';
import { Modal, Select, InputNumber, Switch, Form, message, Button } from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form';
import { connect } from 'react-redux';
import { getCurrency, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { OWButton } from '../ow_button/OWButton';
import { vendorsRestCrudThunksForSupplier } from '../../thunks/vendors_thunks';
import { equipmentCatalogsRestCrudThunksForSupplier } from '../../thunks/equipment_catalogs_thunks';
import { equipmentTypesRestCrudThunksForSupplier } from '../../thunks/equipment_types_thunks';
import OWRichTextEditor from '../../rich_text_editor/OWRichTextEditor';
import ChangeVendorCurrencyComponent from '../vendors_detail_details_page/ChangeVendorCurrencyComponent';
import { useForm } from 'antd/lib/form/Form';
import { isSupplierInventoryConfigEnabled } from '../common/SupplierInventoryConfigPermissionChecker';

const FormItem = Form.Item;
export const DATE_FORMAT = 'MMMM D, YYYY h:mm a';
const FORM_NAME = 'equipment-catalog-form';

interface EquipmentCatalogModalFormProps extends FormComponentProps {
	onSuccess: any;
	onCancel: any;
	createErrors: any;
	updateErrors: any;
	create: any;
	update: any;
	visible: boolean;

	formData: any;
	currentUser: any;
	vendors: any;
	targetCollectionName: any;
	fetchVendors: any;
	fetchMultipleVendors: any;
	equipmentType: any;
	updateEquipment: any;
	companyConfig: any;
}

const EQUIPMENT_CATALOG_FORM_VENDORS_AUTOCOMPLETE = 'EQUIPMENT_CATALOG_FORM_VENDORS_AUTOCOMPLETE';

const EquipmentCatalogModalForm: FC<EquipmentCatalogModalFormProps> = ({
	create,
	update,
	onSuccess,
	onCancel,
	formData,
	vendors,
	currentUser,
	fetchVendors,
	fetchMultipleVendors,
	equipmentType,
	updateEquipment,
	companyConfig,
}): React.ReactElement => {
	const [saving, setSaving] = useState(false);

	const canEditLastPrice = useMemo(
		() => isSupplierInventoryConfigEnabled(companyConfig, 'allowToEditLastPrice'),
		[companyConfig]
	);

	const editorRef = useRef(null);

	const [form] = useForm();

	const partEquipmentVendorId = Form.useWatch('partEquipmentVendorId', form);
	const listPrice = Form.useWatch('listPrice', form);

	const updateDefaultVendorIfApplicable = useCallback(
		(values) => {
			return new Promise((resolve, reject) => {
				if (values.isDefaultVendor) {
					updateEquipment({
						...equipmentType,
						defaultVendorId: values.partEquipmentVendorId,
					})
						.then((res) => resolve(res))
						.catch((err) => reject(err));
				} else {
					resolve(true);
				}
			});
		},
		[equipmentType, updateEquipment]
	);

	const getNotes = useCallback(
		() => nullSafeGet('current', editorRef) && editorRef.current.getContent(),
		[]
	);

	const currencyId = useMemo(() => {
		const currency = getCurrency({ currentUser });
		return nullSafeGetOrElse(`records.${partEquipmentVendorId}.currencyId`, vendors, currency.id);
	}, [currentUser, partEquipmentVendorId, vendors]);

	const handleSubmit = useCallback(
		(values) => {
			setSaving(true);
			const submitAction = values.id ? update : create;
			Promise.all([
				updateDefaultVendorIfApplicable(values),
				submitAction({
					...values,
					notes: getNotes(),
					...(values.listPrice && { listPrice: `${values.listPrice}` }),
					...(canEditLastPrice && values.lastPrice && { lastPrice: `${values.lastPrice}` }),
					currencyId,
				}),
			])
				.then(() => {
					onSuccess();
				})
				.catch((err) => message.error(err))
				.finally(() => setSaving(false));
		},
		[
			update,
			create,
			updateDefaultVendorIfApplicable,
			getNotes,
			canEditLastPrice,
			currencyId,
			onSuccess,
		]
	);

	const isUpdate = useMemo(() => !!formData.id, [formData.id]);

	const isFirstVendor = useMemo(
		() => equipmentType.partEquipmentVendorCount <= 0,
		[equipmentType.partEquipmentVendorCount]
	);

	const isDefaultVendor = useMemo(
		() =>
			!!formData.partEquipmentVendorId &&
			formData.partEquipmentVendorId === nullSafeGet('defaultVendorId', equipmentType),
		[formData.partEquipmentVendorId, equipmentType]
	);

	return (
		<Modal
			visible={true}
			width={600}
			title={`${isUpdate ? 'Edit' : 'Add'} Vendor for Equipment`}
			onCancel={onCancel}
			closable={true}
			footer={[
				<Button onClick={onCancel} size="large">
					Cancel
				</Button>,
				<OWButton
					type="primary"
					size="large"
					style={{ marginLeft: '16px' }}
					key="submit"
					htmlType="submit"
					form={FORM_NAME}
					loading={saving}
				>
					{isUpdate ? 'Update' : 'Create'}
				</OWButton>,
			]}
		>
			<Form
				layout="vertical"
				initialValues={formData}
				onFinish={handleSubmit}
				form={form}
				id={FORM_NAME}
			>
				<FormItem name="id" hidden />
				<FormItem name="equipmentTypeId" hidden />
				<FormItem
					label="Vendor"
					name="partEquipmentVendorId"
					rules={[
						{
							required: true,
							message: 'Please select a vendor.',
						},
					]}
				>
					<OWAsyncSelect
						disabled={isUpdate}
						style={{ maxWidth: 300 }}
						stateSlice={vendors}
						targetCollectionName={EQUIPMENT_CATALOG_FORM_VENDORS_AUTOCOMPLETE}
						fetchMultiple={(ids, targetCollectionName) => {
							fetchMultipleVendors(ids, targetCollectionName);
						}}
						fetchData={(
							searchText,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) =>
							fetchVendors(
								{ name: searchText },
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							)
						}
						renderRecord={(vendor) => (
							<Select.Option key={vendor.id} value={vendor.id}>
								{vendor.name}
							</Select.Option>
						)}
					/>
				</FormItem>
				<FormItem
					label="Is Default Vendor"
					name="isDefaultVendor"
					valuePropName={'checked'}
					initialValue={isDefaultVendor}
				>
					<Switch />
				</FormItem>
				<div className="flex flex-row items-center justify-between">
					<FormItem
						label="List Price"
						name="listPrice"
						rules={[
							{
								...(isFirstVendor && { required: true }),
								message: !listPrice
									? 'Cost of the part will be removed with the addition of this vendor'
									: '',
								warningOnly: true,
							},
						]}
						initialValue={formData.listPrice || (isFirstVendor ? equipmentType.cost : undefined)}
					>
						<InputNumber
							style={{ width: 320 }}
							min={0.0}
							precision={2}
							step={0.01}
							addonAfter={currencyId}
						/>
					</FormItem>
					<div>
						{partEquipmentVendorId ? (
							<ChangeVendorCurrencyComponent vendorId={partEquipmentVendorId} />
						) : null}
					</div>
				</div>
				{canEditLastPrice && (
					<FormItem label="Last Price" name="lastPrice" initialValue={formData.lastPrice}>
						<InputNumber
							style={{ width: 320 }}
							min={0.0}
							precision={2}
							step={0.01}
							addonAfter={currencyId}
						/>
					</FormItem>
				)}
				<FormItem label="Notes" name="notes">
					<OWRichTextEditor initialEditorState={formData.notes} editorRef={editorRef} />
				</FormItem>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state, ownProps) => ({
	visible: ownProps.visible,
	onCancel: ownProps.onCancel,
	onSuccess: ownProps.onSuccess,
	formData: ownProps.formData,
	vendors: state.vendors,
	currentUser: state.session.currentUser,
	targetCollectionName: ownProps.targetCollectionName,
	createErrors: state.buyer_contacts.createErrors,
	updateErrors: state.buyer_contacts.updateErrors,
	equipmentType: state.equipment_types.detail,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	create: (entity, targetCollectionName) =>
		dispatch(equipmentCatalogsRestCrudThunksForSupplier.create(entity, 'id', targetCollectionName)),
	update: (entity) => dispatch(equipmentCatalogsRestCrudThunksForSupplier.update(entity)),
	fetchVendors: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			vendorsRestCrudThunksForSupplier.read(
				{ ...(params || {}), isActive: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleVendors: (ids, targetCollectionName) =>
		dispatch(vendorsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	updateEquipment: (entity) => dispatch(equipmentTypesRestCrudThunksForSupplier.update(entity)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EquipmentCatalogModalForm);
